import React, { createContext, useContext, useRef, useCallback, useMemo } from 'react';
import type { Product } from '../types/product';
import { logger } from '../../utils/logger';

interface ProductCacheContextType {
  getProduct: (productSku: string, apiBaseUrl: string) => Promise<Product | null>;
  getProducts: (productSkus: string[], apiBaseUrl: string) => Promise<Record<string, Product>>;
}

const ProductCacheContext = createContext<ProductCacheContextType | null>(null);

export function ProductCacheProvider({
  children,
  initialProducts,
}: {
  children: React.ReactNode;
  initialProducts?: Record<string, Product>;
}) {
  // Cache stores fetched products - persists across renders
  const cache = useRef<Record<string, Product>>(initialProducts || {});
  // Track in-flight requests to avoid duplicate fetches
  const pending = useRef<Record<string, Promise<Product | null>>>({});

  // Merge new initialProducts into cache on updates (useRef only captures
  // initial value on mount, so when a conversation updates via SSE with new
  // products, we need to merge them in).
  if (initialProducts) {
    for (const sku in initialProducts) {
      if (!(sku in cache.current)) {
        cache.current[sku] = initialProducts[sku];
      }
    }
  }

  const getProduct = useCallback(async (productSku: string, apiBaseUrl: string): Promise<Product | null> => {
    // Return from cache if available
    if (cache.current[productSku]) {
      return cache.current[productSku];
    }

    // If already fetching this product, wait for that request
    if (productSku in pending.current) {
      return pending.current[productSku];
    }

    // Start new fetch
    const fetchPromise = (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/product/${productSku}`, {
          headers: {
            'ngrok-skip-browser-warning': 'true'
          }
        });
        if (response.ok) {
          const data = await response.json();
          cache.current[productSku] = data;
          return data;
        }
        return null;
      } catch (error) {
        logger.error(`[ProductCache] Error fetching product ${productSku}`, { sku: productSku, errorMessage: error instanceof Error ? error.message : String(error) });
        return null;
      } finally {
        // Clean up pending reference
        delete pending.current[productSku];
      }
    })();

    pending.current[productSku] = fetchPromise;
    return fetchPromise;
  }, []);

  const getProducts = useCallback(async (productSkus: string[], apiBaseUrl: string): Promise<Record<string, Product>> => {
    const results: Record<string, Product> = {};

    // Fetch all products in parallel (cache handles deduplication)
    const promises = productSkus.map(async (sku) => {
      const product = await getProduct(sku, apiBaseUrl);
      if (product) {
        results[sku] = product;
      }
    });

    await Promise.all(promises);
    return results;
  }, [getProduct]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ getProduct, getProducts }), [getProduct, getProducts]);

  return (
    <ProductCacheContext.Provider value={contextValue}>
      {children}
    </ProductCacheContext.Provider>
  );
}

export function useProductCache() {
  const context = useContext(ProductCacheContext);
  if (!context) {
    throw new Error('useProductCache must be used within a ProductCacheProvider');
  }
  return context;
}

// Optional hook for components that might be used outside the provider
export function useProductCacheOptional() {
  return useContext(ProductCacheContext);
}

